/**
 * EquipmentPackageStepV2 — E-105 3-oszlopos rental wizard V2 layout
 *
 * 3-oszlopos layout (300px / flex-1 / 300px) replaces the accordion-pattern
 * split between EquipmentStep + PackageAndAccessoriesStep.
 *
 * **Mockup ratified 2026-05-11** (`feedback-rental-ui-v2-verbal-ratify-2026-05-11` memory):
 *   _bmad-output/reports/mockups/rental-multi-machine/02-gepek-csomag-v2.html
 *
 * **ADR-103 5-panel accordion SUPERSEDED** by Sprint 20 W0 ratify (Amendment-1).
 *
 * **Feature flag**: `RENTAL_WIZARD_V2_LAYOUT` (default OFF, opt-in via
 * localStorage `feature.rentalWizardV2=true` during scaffold iteration).
 *
 * **Phase progress**:
 * - Phase 1 (D2, commit `1db36184`): scaffold + flag wiring ✅
 * - Phase 5 (D3, commit `1ee6dc82`): RentalWizardPage flag-gated integration ✅
 * - **Phase 2 (D4, this PR)**: left column real data wiring
 *   (category chips + condition chips + local filter state)
 * - Phase 3: main column (equipment list + package builder) real data wiring
 * - Phase 4: right column (sticky summary) real data wiring
 *
 * **NEM scope** (Sprint 20+ deferred):
 * - Backend `RentalContract` cardinality migration (ADR-118)
 * - Date-range picker (Phase 2 sub-slice, ~1 SP separate PR)
 * - Saved filter presets (needs backend persistence)
 * - Mobile responsive breakpoints (mockup desktop-only 1440px)
 *
 * **Backward compatibility**: when flag OFF (default prod), this component
 * NEM renderelt. Accordion EquipmentStep + PackageAndAccessoriesStep marad.
 */

import type { Package, PackageItemCategory } from '@/api/packages';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { usePackagesForEquipment } from '@/hooks/use-packages';
import { useRentalCalcPricing } from '@/hooks/use-rental-calc-pricing';
import { useBergep } from '@/hooks/use-rental-equipment';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { RentalLastRentalBanner } from '../components/RentalLastRentalBanner';
import { buildRentalCalcRequest } from '../lib/build-rental-calc-request';
import { formatCurrency } from '../mock-data';
import type { CartItem, Equipment } from '../types';
import {
  CATEGORY_LABEL_KEYS,
  CONDITION_LABEL_KEYS,
  EQUIPMENT_STATUS_COLORS,
  EquipmentCategory,
  EquipmentCondition,
  EquipmentStatus,
  STATUS_LABEL_KEYS,
} from '../types';
import { useWizardStore } from '../wizard-store';
import { DayBreakdownSummary } from './DayBreakdownSummary';
import { prioritizeAvailable } from './equipment-availability';

/**
 * Sprint 26 W1 D-5 evening B build — V2EquipmentPicker: minimal-port of V1
 * `EquipmentStep` search-flow into V2 main column. Server-side debounced
 * search via `useBergep` (250ms), category + condition filter inheritance
 * from `V2LeftSidebar` chips (wizard-store Sets), click-to-cart via
 * `addToCart`. Phase 3f scaffold-completion.
 *
 * Duplicate `toWizardEquipment` helper (V1 EquipmentStep.tsx:25) — extract
 * to `useEquipmentPicker()` shared hook Sprint 27+ retire-by gate per
 * Amelia party-mode "duplikált JSX MOST" precedens.
 */
function toV2WizardEquipment(item: {
  id: string;
  name: string;
  inventoryCode?: string;
  serialNumber: string;
  category: string;
  brand?: string;
  model?: string;
  status: string;
  condition: string;
  dailyRate: number;
  weeklyRate: number;
  monthlyRate: number;
  hourlyRate?: number;
  depositAmount: number;
}): Equipment {
  const equipment: Equipment = {
    id: item.id,
    name: item.name,
    equipmentCode: item.inventoryCode,
    serialNumber: item.serialNumber,
    category: (item.category as EquipmentCategory) ?? EquipmentCategory.POWER_TOOL,
    brand: item.brand,
    model: item.model,
    status: (item.status as EquipmentStatus) ?? EquipmentStatus.AVAILABLE,
    condition: (item.condition as EquipmentCondition) ?? EquipmentCondition.GOOD,
    dailyRate: item.dailyRate,
    weeklyRate: item.weeklyRate,
    monthlyRate: item.monthlyRate,
    depositAmount: item.depositAmount,
  };
  if (item.hourlyRate != null && item.hourlyRate > 0) {
    equipment.hourlyRate = item.hourlyRate;
  }
  return equipment;
}

export function EquipmentPackageStepV2() {
  return (
    <div className="flex h-full flex-col bg-bg" data-testid="rental-wizard-v2-root">
      <V2Header />
      <RentalLastRentalBanner />
      <V2Legend />
      <div className="flex flex-1 gap-4 p-4 overflow-hidden">
        <V2LeftSidebar />
        <V2MainContent />
        <V2RightSidebar />
      </div>
    </div>
  );
}

/**
 * FE-B/2 — settlement-osztály badge (Ingyenes / Bérelhető / Bizományos) egy
 * csomag-item kategóriájához. A V2Legend színkódját tükrözi item-szinten.
 */
const SETTLEMENT_BADGE: Record<
  PackageItemCategory,
  { dot: string; cls: string; labelKey: string }
> = {
  FREE: {
    dot: 'bg-emerald-500',
    cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    labelKey: 'rental.wizard.v2.legend.free',
  },
  RENTABLE: {
    dot: 'bg-blue-500',
    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    labelKey: 'rental.wizard.v2.legend.rentable',
  },
  CONSIGNMENT: {
    dot: 'bg-amber-500',
    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    labelKey: 'rental.wizard.v2.legend.consignment',
  },
};

export function SettlementBadge({ category }: { category: PackageItemCategory }) {
  const { t } = useTranslation();
  const cfg = SETTLEMENT_BADGE[category];
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium',
        cfg.cls
      )}
      data-testid={`settlement-badge-${category}`}
    >
      {t(cfg.labelKey)}
    </span>
  );
}

/**
 * FE-B/2 + FE-B/3 — csomag-tartalom-bontás: egy kiválasztott csomag item-listája
 * settlement-badge-dzsel. A `Package.items`-ből (feloldva a selection packageId-ből).
 *
 * `editable` (FE-B/3): per-item qty-stepper, ami a wizard-store
 * `setSelectionItemQty`-t hívja (kosár-mutáció). Nélküle render-only (FE-B/2).
 *
 * `showLineTotal` (slice-2, default: false): ha true, a sorhoz mutatja a sor-összeget
 * (unitPrice × qty). Rentable: Ft/nap × qty; Consignment: Ft/db × qty (bizományos jelzéssel);
 * Free: freeInPkg szöveg. NE add meg a régi V2-ben — backward-compatible, 0-regresszió.
 */
export function PackageContentBreakdown({
  pkg,
  editable,
  showLineTotal = false,
}: {
  pkg: Package | undefined;
  editable?: {
    selectionId: string;
    qtyOverrides?: Record<string, number>;
    onQtyChange: (selectionId: string, itemId: string, qty: number) => void;
  };
  showLineTotal?: boolean;
}) {
  const { t } = useTranslation();
  if (!pkg || pkg.items.length === 0) return null;
  return (
    <ul
      className="mt-1.5 space-y-1 border-l border-border pl-2"
      data-testid={`package-breakdown-${pkg.id}`}
    >
      {pkg.items.map(item => {
        const qty = editable?.qtyOverrides?.[item.id] ?? item.quantity;

        // Sor-összeg számítás (showLineTotal=true esetén)
        let lineTotalNode: React.ReactNode = null;
        if (showLineTotal) {
          if (item.category === 'FREE') {
            lineTotalNode = (
              <span
                className="shrink-0 text-emerald-600 dark:text-emerald-400 text-[10px] font-medium"
                data-testid={`package-item-line-total-${item.id}`}
              >
                {t('rental.wizard.v2.detail.freeInPkg')}
              </span>
            );
          } else if (item.category === 'RENTABLE' && item.unitPrice != null) {
            const total = item.unitPrice * qty;
            lineTotalNode = (
              <span
                className="shrink-0 tabular-nums text-[10px] font-semibold text-foreground"
                data-testid={`package-item-line-total-${item.id}`}
              >
                {total.toLocaleString('hu-HU')} {t('rental.wizard.v2.detail.rentableUnit')}
              </span>
            );
          } else if (item.category === 'CONSIGNMENT' && item.unitPrice != null) {
            const total = item.unitPrice * qty;
            lineTotalNode = (
              <span
                className="shrink-0 tabular-nums text-[10px] font-semibold text-amber-700 dark:text-amber-400"
                data-testid={`package-item-line-total-${item.id}`}
              >
                {total.toLocaleString('hu-HU')} {t('rental.wizard.v2.detail.consignUnit')}
              </span>
            );
          }
        }

        return (
          <li
            key={item.id}
            className="flex items-center gap-2 text-[11px] text-foreground"
            data-testid={`package-item-${item.id}`}
          >
            <span className="flex-1 truncate">
              {(item.productId &&
                pkg.linkedProducts?.find(lp => lp.productId === item.productId)?.product.name) ||
                item.notes?.trim() ||
                t('rental.wizard.v2.unnamedItem')}
            </span>
            <SettlementBadge category={item.category} />
            {showLineTotal && lineTotalNode}
            {editable ? (
              <PackageItemStepper
                qty={qty}
                onDec={() =>
                  editable.onQtyChange(editable.selectionId, item.id, Math.max(0, qty - 1))
                }
                onInc={() => editable.onQtyChange(editable.selectionId, item.id, qty + 1)}
                itemId={item.id}
              />
            ) : (
              <span
                className="shrink-0 text-muted-foreground"
                data-testid={`package-item-qty-${item.id}`}
              >
                {t('rental.wizard.v2.itemQty', { qty })}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * FE-B/3 — per-tartozék mennyiség-stepper (a V1 AccessorySuggestionPanel stepper
 * portja). A +/- a wizard-store `setSelectionItemQty`-jét hívja a szülőn át.
 */
function PackageItemStepper({
  qty,
  onDec,
  onInc,
  itemId,
}: {
  qty: number;
  onDec: () => void;
  onInc: () => void;
  itemId: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={cn('flex shrink-0 items-center gap-1', qty === 0 && 'opacity-50')}
      data-testid={`package-item-stepper-${itemId}`}
    >
      <button
        type="button"
        onClick={onDec}
        className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-muted-foreground hover:bg-muted"
        aria-label={t('rental.wizard.v2.itemQtyDec')}
        data-testid={`package-item-dec-${itemId}`}
      >
        −
      </button>
      <span className="w-6 text-center tabular-nums" data-testid={`package-item-qty-${itemId}`}>
        {qty}
      </span>
      <button
        type="button"
        onClick={onInc}
        className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-muted-foreground hover:bg-muted"
        aria-label={t('rental.wizard.v2.itemQtyInc')}
        data-testid={`package-item-inc-${itemId}`}
      >
        +
      </button>
    </div>
  );
}

/**
 * FE-B/1 — settlement-osztály jelmagyarázat (Ingyenes / Bérelhető / Bizományos).
 * A step-2 tetején a kártya- és csomag-tartalom settlement-badge-ek színkódját
 * magyarázza. Render-only; a tényleges badge-eket az egyes item-ek hordozzák.
 * Proto-ref: rental-step-wizard-v3-2026-06-01.html „Jelmagyarázat:".
 */
export function V2Legend() {
  const { t } = useTranslation();
  const items: { key: string; dot: string; label: string }[] = [
    { key: 'free', dot: 'bg-emerald-500', label: t('rental.wizard.v2.legend.free') },
    { key: 'rentable', dot: 'bg-blue-500', label: t('rental.wizard.v2.legend.rentable') },
    { key: 'consignment', dot: 'bg-amber-500', label: t('rental.wizard.v2.legend.consignment') },
  ];
  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border px-6 py-2 text-xs text-muted-foreground"
      data-testid="rental-wizard-v2-legend"
      aria-label={t('rental.wizard.v2.legend.title')}
    >
      <span className="font-medium">{t('rental.wizard.v2.legend.title')}</span>
      {items.map(it => (
        <span key={it.key} className="inline-flex items-center gap-1.5">
          <span className={cn('h-2.5 w-2.5 rounded-full', it.dot)} aria-hidden="true" />
          {it.label}
        </span>
      ))}
    </div>
  );
}

/**
 * FE-B/4 — szűrő-tab count-badge: hány kosárbeli gép esik az adott
 * kategória/állapot szűrő-chipbe. 0-nál nem renderel (zaj-mentes).
 */
export function FilterCountBadge({ count, testid }: { count: number; testid: string }) {
  if (count <= 0) return null;
  return (
    <span
      className="ml-1.5 inline-flex min-w-4 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-medium text-muted-foreground tabular-nums"
      data-testid={testid}
    >
      {count}
    </span>
  );
}

/**
 * V2 sub-header — wizard step indicator + draft autosave marker.
 * Mockup ref: lines 53-90 (header section).
 */
function V2Header() {
  const { t } = useTranslation();
  return (
    <header
      className="bg-surface border-b border-border flex items-center justify-between px-6 py-3"
      data-testid="rental-wizard-v2-header"
    >
      <div className="text-sm font-semibold">{t('rental.wizard.v2.headerTitle')}</div>
      <div className="text-[11px] text-muted-foreground">
        {/* TODO Phase 2: WizardSubHeader integráció (step indicator + draft autosave) */}
        {t('rental.wizard.v2.headerSubtitle')}
      </div>
    </header>
  );
}

/**
 * Left column (300px fixed) — Filter sidebar.
 *
 * Phase 2 (D4): category + condition multi-select chips (local useState).
 * Phase 3e (D10): filter state lifted to wizard-store — persists across panel
 * toggles + V1↔V2 flag flips, applied to V2MainContent cart filter.
 *
 * Mockup ref: 02-gepek-csomag-v2.html lines 100-200 (filter section).
 */
function V2LeftSidebar() {
  const { t } = useTranslation();
  const { cart, categories, conditions, toggleCategory, toggleCondition, clearFilters } =
    useWizardStore(
      useShallow(s => ({
        cart: s.cart,
        categories: s.v2FilterCategories,
        conditions: s.v2FilterConditions,
        toggleCategory: s.toggleV2FilterCategory,
        toggleCondition: s.toggleV2FilterCondition,
        clearFilters: s.clearV2Filters,
      }))
    );
  const hasActiveFilters = categories.size > 0 || conditions.size > 0;

  // FE-B/4: count-badge — hány kosárbeli gép esik az adott kategóriába / állapotba.
  const { catCounts, condCounts } = useMemo(() => {
    const cat: Record<string, number> = {};
    const cond: Record<string, number> = {};
    for (const item of cart) {
      const c = item.equipment.category;
      if (c) cat[c] = (cat[c] ?? 0) + 1;
      const s = item.equipment.condition;
      if (s) cond[s] = (cond[s] ?? 0) + 1;
    }
    return { catCounts: cat, condCounts: cond };
  }, [cart]);

  return (
    <aside
      className="w-[300px] shrink-0 bg-surface border border-border rounded-md p-4 overflow-y-auto"
      data-testid="rental-wizard-v2-left"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t('rental.wizard.v2.filtersTitle')}</h3>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-[11px] text-muted-foreground hover:text-fg underline tap-sm"
            data-testid="rental-wizard-v2-clear-filters"
          >
            {t('rental.wizard.v2.clearFilters')}
          </button>
        )}
      </div>

      {/* Category chips section */}
      <section className="mb-4" data-testid="rental-wizard-v2-categories">
        <h4 className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
          {t('rental.wizard.v2.categoryHeader')}
        </h4>
        <div className="flex flex-wrap gap-1.5">
          {Object.values(EquipmentCategory).map(cat => {
            const isSelected = categories.has(cat);
            return (
              <button
                key={cat}
                type="button"
                onClick={() => toggleCategory(cat)}
                className={cn(
                  'tap-sm px-2.5 py-1 text-xs rounded-full border transition-colors',
                  isSelected
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-surface border-border hover:border-border-strong'
                )}
                aria-pressed={isSelected}
                data-testid={`rental-wizard-v2-category-chip-${cat}`}
              >
                {t(CATEGORY_LABEL_KEYS[cat])}
                <FilterCountBadge count={catCounts[cat] ?? 0} testid={`category-count-${cat}`} />
              </button>
            );
          })}
        </div>
      </section>

      {/* Condition chips section */}
      <section className="mb-4" data-testid="rental-wizard-v2-conditions">
        <h4 className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
          {t('rental.wizard.v2.conditionHeader')}
        </h4>
        <div className="flex flex-wrap gap-1.5">
          {Object.values(EquipmentCondition).map(cond => {
            const isSelected = conditions.has(cond);
            return (
              <button
                key={cond}
                type="button"
                onClick={() => toggleCondition(cond)}
                className={cn(
                  'tap-sm px-2.5 py-1 text-xs rounded-full border transition-colors',
                  isSelected
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-surface border-border hover:border-border-strong'
                )}
                aria-pressed={isSelected}
                data-testid={`rental-wizard-v2-condition-chip-${cond}`}
              >
                {t(CONDITION_LABEL_KEYS[cond])}
                <FilterCountBadge
                  count={condCounts[cond] ?? 0}
                  testid={`condition-count-${cond}`}
                />
              </button>
            );
          })}
        </div>
      </section>

      {/* Deferred sections — Phase 2 sub-slices */}
      <section className="text-[11px] text-muted-foreground p-2 border border-dashed border-border rounded">
        {t('rental.wizard.v2.deferredFilters')} Elérhetőség dátum, mentett szűrő presetek
      </section>
    </aside>
  );
}

/**
 * Main column (flex-1) — Equipment list + package builder.
 *
 * Phase 3a (D5): Equipment card list scaffolding — cart.map → EquipmentCardV2
 * with name + serial + category label + status badge + quantity + remove.
 *
 * Phase 3 sub-slice progress:
 * - 3a (D5): Equipment card list ✅
 * - 3b (D6): Package chips display (selections array) ✅
 * - 3c (D7): "Csomag hozzáadása" dropdown ✅
 * - 3d (D8): Duplicate soft-guard toast ✅
 * - 3e (D10, this commit): Filter state lift local → wizard-store + apply to cart ✅
 *
 * Mockup ref: 02-gepek-csomag-v2.html main content section (equipment cards).
 */
function V2MainContent() {
  const { t } = useTranslation();
  const { cart, filterCategories, filterConditions } = useWizardStore(
    useShallow(s => ({
      cart: s.cart,
      filterCategories: s.v2FilterCategories,
      filterConditions: s.v2FilterConditions,
    }))
  );

  // Phase 3e: apply V2 filters to cart card list. Empty Set = NO narrowing
  // (show all cart items). When V2 search slice (Phase 3f) lands, the same
  // filter Sets will narrow browse/search equipment results too.
  const filteredCart = cart.filter(item => {
    const catMatch = filterCategories.size === 0 || filterCategories.has(item.equipment.category);
    const condMatch = filterConditions.size === 0 || filterConditions.has(item.equipment.condition);
    return catMatch && condMatch;
  });
  const hasActiveFilters = filterCategories.size > 0 || filterConditions.size > 0;
  const isFilteredEmpty = cart.length > 0 && filteredCart.length === 0;

  return (
    <main
      className="flex-1 bg-surface border border-border rounded-md p-4 overflow-y-auto flex flex-col gap-3"
      data-testid="rental-wizard-v2-main"
    >
      {/* Sprint 26 W1 D-5 evening B build — V2 add-equipment picker (Phase 3f).
          Always visible at top of main column, regardless of cart state. */}
      <V2EquipmentPicker />

      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t('rental.wizard.v2.cartTitle')}</h3>
        <span
          className="text-[11px] text-muted-foreground"
          data-testid="rental-wizard-v2-cart-count"
        >
          {cart.length === 0
            ? t('rental.wizard.v2.cartCount', { count: 0 })
            : hasActiveFilters
              ? t('rental.wizard.v2.cartCountFiltered', {
                  shown: filteredCart.length,
                  total: cart.length,
                })
              : t('rental.wizard.v2.cartCount', { count: cart.length })}
        </span>
      </header>

      {cart.length === 0 ? (
        <div
          className="text-xs text-muted-foreground italic text-center py-6"
          data-testid="rental-wizard-v2-cart-empty"
        >
          {t('rental.wizard.v2.emptyCart')}
        </div>
      ) : isFilteredEmpty ? (
        <div
          className="text-xs text-muted-foreground italic text-center py-6"
          data-testid="rental-wizard-v2-cart-filtered-empty"
        >
          {t('rental.wizard.v2.cartFilteredEmpty')}
        </div>
      ) : (
        <div className="space-y-2" data-testid="rental-wizard-v2-cart-list">
          {filteredCart.map(cartItem => (
            <EquipmentCardV2 key={cartItem.id} cartItem={cartItem} />
          ))}
        </div>
      )}
    </main>
  );
}

/**
 * V2EquipmentPicker — search-driven equipment picker, V1 EquipmentStep
 * minimal-port. Sprint 26 W1 D-5 evening B build (Phase 3f scaffold-
 * completion). Server-side debounced search (250ms) via `useBergep`,
 * category + condition filter chip inheritance from V2LeftSidebar
 * (wizard-store Sets), click-to-cart via `addToCart`.
 *
 * Reuse-pattern (Amelia party-mode): inline JSX duplication NOW,
 * `useEquipmentPicker()` shared hook extract Sprint 27+ retire-by gate.
 */
function V2EquipmentPicker() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { addToCart, isInCart, filterCategories, filterConditions } = useWizardStore(
    useShallow(s => ({
      addToCart: s.addToCart,
      isInCart: s.isInCart,
      filterCategories: s.v2FilterCategories,
      filterConditions: s.v2FilterConditions,
    }))
  );
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 250);

  // Server-side search. If left-sidebar has exactly 1 category chip active,
  // pass it as server filter; multi-chip → fallback to client-side filter.
  const singleCategory = filterCategories.size === 1 ? Array.from(filterCategories)[0] : undefined;

  // party rank-3: availableOnly=false → a backend a RENTED gépeket is visszaadja,
  // hogy a pultos LÁSSA őket (disabled-render: 'Kiadva' jelölés), ne tűnjenek el
  // mintha nem léteznének. A választhatóság-guard a button-szinten + a BE
  // fail-closed (markAsRented compare-and-swap) együtt véd a dupla-kiadás ellen.
  const { equipment: apiEquipment, isLoading } = useBergep({
    availableOnly: false,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(singleCategory ? { category: singleCategory } : {}),
    page: 1,
    pageSize: 20,
  });

  const results = useMemo(() => {
    const filtered = apiEquipment
      .map(toV2WizardEquipment)
      .filter(eq => filterCategories.size === 0 || filterCategories.has(eq.category))
      .filter(eq => filterConditions.size === 0 || filterConditions.has(eq.condition));
    // AVAILABLE-ek előre, hogy a top-8 a választhatókat priorizálja (a RENTED
    // is látszik disabled-ként, ha belefér a limitbe).
    return prioritizeAvailable(filtered).slice(0, 8);
  }, [apiEquipment, filterCategories, filterConditions]);

  const hasQuery = debouncedSearch.length > 0;
  const hasActiveFilters = filterCategories.size > 0 || filterConditions.size > 0;
  // Sprint 26 W2 D-1 chip-only browse enable: results render when EITHER
  // search query OR chip-filter active. Mockup #2 fidelity — left-sidebar
  // chip-click renders filtered list without typing.
  const shouldShowResults = hasQuery || hasActiveFilters;

  const handleAdd = (equipment: Equipment) => {
    // party rank-3 defensive guard: RENTED/non-AVAILABLE gép sosem kerülhet
    // kosárba (a button disabled, de a guard a TOCTOU-stale ág ellen is véd).
    if (equipment.status !== EquipmentStatus.AVAILABLE) {
      return;
    }
    if (isInCart(equipment.id)) {
      toast({
        title: t('rental.wizard.v2.search.alreadyInCart', { name: equipment.name }),
        variant: 'default',
      });
      return;
    }
    addToCart(equipment);
    toast({
      title: t('rental.wizard.v2.search.addedToCart', { name: equipment.name }),
      variant: 'default',
    });
    setSearch('');
  };

  return (
    <section
      className="rounded-md border border-border bg-bg p-3"
      data-testid="rental-wizard-v2-picker"
    >
      <label
        htmlFor="rental-wizard-v2-search"
        className="block text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5"
      >
        {t('rental.wizard.v2.search.label')}
      </label>
      <div className="relative">
        <input
          id="rental-wizard-v2-search"
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('rental.wizard.v2.search.placeholder')}
          className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="rental-wizard-v2-search-input"
          autoComplete="off"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-fg text-xs"
            aria-label={t('rental.wizard.v2.search.clearAriaLabel')}
            data-testid="rental-wizard-v2-search-clear"
          >
            ×
          </button>
        )}
      </div>

      {shouldShowResults && (
        <div className="mt-2" data-testid="rental-wizard-v2-search-results">
          {isLoading ? (
            <div className="text-[11px] text-muted-foreground italic py-2 text-center">
              {t('rental.wizard.v2.search.loading')}
            </div>
          ) : results.length === 0 ? (
            <div
              className="text-[11px] text-muted-foreground italic py-2 text-center"
              data-testid="rental-wizard-v2-search-no-results"
            >
              {hasQuery
                ? t('rental.wizard.v2.search.noResults', { query: debouncedSearch })
                : t('rental.wizard.v2.search.noFilterMatch')}
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border bg-surface">
              {results.map(eq => {
                const inCart = isInCart(eq.id);
                const isAvailable = eq.status === EquipmentStatus.AVAILABLE;
                const disabled = inCart || !isAvailable;
                return (
                  <li key={eq.id}>
                    <button
                      type="button"
                      onClick={() => handleAdd(eq)}
                      disabled={disabled}
                      className={cn(
                        'w-full text-left px-2.5 py-1.5 text-xs hover:bg-accent-soft transition-colors',
                        'flex items-center justify-between gap-2',
                        disabled && 'opacity-50 cursor-not-allowed'
                      )}
                      data-testid={`rental-wizard-v2-search-result-${eq.id}`}
                    >
                      <span className="min-w-0 flex-1 truncate">
                        <span className="font-medium">{eq.name}</span>
                        {eq.serialNumber && (
                          <span className="text-muted-foreground"> — {eq.serialNumber}</span>
                        )}
                      </span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {formatCurrency(eq.dailyRate)}/{t('rental.wizard.perDay')}
                      </span>
                      <span
                        className={cn(
                          'shrink-0 text-[11px] font-semibold',
                          !isAvailable
                            ? 'text-destructive'
                            : inCart
                              ? 'text-muted-foreground'
                              : 'text-accent'
                        )}
                        data-testid={
                          !isAvailable ? `rental-wizard-v2-result-unavailable-${eq.id}` : undefined
                        }
                      >
                        {!isAvailable
                          ? t(STATUS_LABEL_KEYS[eq.status])
                          : inCart
                            ? t('rental.wizard.v2.search.alreadyAdded')
                            : t('rental.wizard.v2.search.addAction')}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {!shouldShowResults && (
        <p className="mt-1.5 text-[11px] text-muted-foreground italic">
          {t('rental.wizard.v2.search.hint')}
        </p>
      )}
    </section>
  );
}

interface EquipmentCardV2Props {
  cartItem: CartItem;
}

/**
 * Equipment card in V2 main column. Single equipment from cart.
 *
 * Phase 3a (D5): name + serial + category label + status badge + quantity + remove.
 * Phase 3b (D6): + package chips display (read + remove from packageSelections).
 * Phase 3c (next): + "Csomag hozzáadása" dropdown (usePackagesForEquipment hook).
 */
function EquipmentCardV2({ cartItem }: EquipmentCardV2Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const removeFromCart = useWizardStore(s => s.removeFromCart);
  const selections = useWizardStore(s => s.getSelectionsForEquipment(cartItem.equipment.id));
  const removePackageSelection = useWizardStore(s => s.removePackageSelection);
  const addPackageSelection = useWizardStore(s => s.addPackageSelection);
  const setSelectionItemQty = useWizardStore(s => s.setSelectionItemQty);
  const hasDuplicateSelection = useWizardStore(s => s.hasDuplicateSelection);
  const { packages, isLoading: isPackagesLoading } = usePackagesForEquipment(cartItem.equipment.id);
  const { equipment, quantity } = cartItem;
  const statusColor = EQUIPMENT_STATUS_COLORS[equipment.status];
  const statusLabel = t(STATUS_LABEL_KEYS[equipment.status]);
  const categoryLabel = t(CATEGORY_LABEL_KEYS[equipment.category]);

  const handleAddPackage = (pkg: Package) => {
    // Phase 3d: duplicate soft-guard (Winston-J3 pattern, V1 port).
    // Warn but allow — "2 készlet védőfelszerelés" is a valid pultos action.
    if (hasDuplicateSelection(equipment.id, pkg.id)) {
      toast({
        title: t('rental.wizard.v2.duplicateTitle'),
        description: t('rental.wizard.v2.duplicateDesc', { name: pkg.name }),
      });
    }
    addPackageSelection({
      equipmentId: equipment.id,
      packageId: pkg.id,
      packageName: pkg.name,
      packageCode: pkg.packageCode,
    });
  };

  return (
    <article
      className="bg-bg border border-border rounded-md p-3 hover:border-border-strong transition-colors"
      data-testid={`rental-wizard-v2-card-${cartItem.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold truncate">{equipment.name}</h4>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-muted-foreground" data-testid="card-serial">
              SN: {equipment.serialNumber}
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="text-[11px] text-muted-foreground" data-testid="card-category">
              {categoryLabel}
            </span>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5">
          <span
            className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium', statusColor)}
            data-testid="card-status-badge"
          >
            {statusLabel}
          </span>
          {quantity > 1 && (
            <span className="text-[10px] text-muted-foreground" data-testid="card-quantity">
              {quantity} db
            </span>
          )}
        </div>
      </div>

      {/* Phase 3b: package chips per equipment (read + remove via store). */}
      <div className="mt-2" data-testid="card-packages">
        {selections.length === 0 ? (
          <div
            className="text-[11px] text-muted-foreground italic"
            data-testid="card-packages-empty"
          >
            {t('rental.wizard.v2.packagesEmpty')}
          </div>
        ) : (
          <ul className="flex flex-wrap items-center gap-1.5" data-testid="card-packages-list">
            {selections.map(sel => (
              <li key={sel.selectionId} className="w-full">
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full bg-accent-soft text-accent border border-accent/20"
                  data-testid={`card-package-chip-${sel.selectionId}`}
                >
                  <span className="font-medium">{sel.packageName}</span>
                  <button
                    type="button"
                    onClick={() => removePackageSelection(sel.selectionId, 'user-action')}
                    className="text-accent hover:text-red-600 transition-colors -mr-0.5 leading-none"
                    aria-label={t('rental.wizard.v2.packageRemoveAriaLabel', {
                      name: sel.packageName,
                    })}
                    data-testid={`card-package-remove-${sel.selectionId}`}
                  >
                    ×
                  </button>
                </span>
                {/* FE-B/2+3: csomag-tartalom-bontás settlement-badge + qty-stepper */}
                <PackageContentBreakdown
                  pkg={packages.find(p => p.id === sel.packageId)}
                  editable={{
                    selectionId: sel.selectionId,
                    ...(sel.qtyOverrides ? { qtyOverrides: sel.qtyOverrides } : {}),
                    onQtyChange: setSelectionItemQty,
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Phase 3c: "Csomag hozzáadása" dropdown — V1 PackagePicker pattern ported. */}
      <div className="mt-2" data-testid="card-package-picker">
        <V2PackagePicker
          packages={packages}
          isLoading={isPackagesLoading}
          onAdd={handleAddPackage}
        />
      </div>

      <div className="mt-2 flex items-center justify-end">
        <button
          type="button"
          onClick={() => removeFromCart(cartItem.id)}
          className="text-[11px] text-muted-foreground hover:text-red-600 transition-colors tap-sm"
          aria-label={t('rental.wizard.v2.removeAriaLabel', { name: equipment.name })}
          data-testid="card-remove"
        >
          {t('rental.wizard.v2.removeButton')}
        </button>
      </div>
    </article>
  );
}

interface V2PackagePickerProps {
  packages: Package[];
  isLoading: boolean;
  onAdd: (pkg: Package) => void;
}

/**
 * Compact package picker for V2 card. Closed → "+ Csomag" button; open →
 * inline listbox of available packages. Mockup-fidelity: smaller footprint
 * than V1 PackagePicker (which lives in its own Card).
 *
 * Phase 3d (next) will wrap onAdd with duplicate soft-guard toast
 * (Winston-J3 pattern from V1 PackageAndAccessoriesStep).
 */
function V2PackagePicker({ packages, isLoading, onAdd }: V2PackagePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (isLoading) {
    return (
      <div
        className="text-[11px] text-muted-foreground italic"
        role="status"
        aria-live="polite"
        data-testid="card-package-picker-loading"
      >
        {t('rental.wizard.v2.packagesLoading')}
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div
        className="text-[11px] text-muted-foreground italic"
        data-testid="card-package-picker-empty"
      >
        {t('rental.wizard.v2.packagesNoneAvailable')}
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="listbox"
        aria-expanded={false}
        className="text-[11px] text-accent hover:underline tap-sm"
        data-testid="card-package-picker-trigger"
      >
        + {t('rental.wizard.v2.addPackageButton')}
      </button>
    );
  }

  return (
    <div
      className="border border-border rounded-md bg-surface max-h-48 overflow-y-auto shadow-soft"
      role="listbox"
      aria-label={t('rental.wizard.v2.addPackageButton')}
      data-testid="card-package-picker-listbox"
    >
      <div className="flex items-center justify-between border-b border-border px-2 py-1.5 bg-bg/50">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {t('rental.wizard.v2.addPackageButton')}
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={t('rental.wizard.v2.closePickerAriaLabel')}
          className="text-[11px] text-muted-foreground hover:text-fg tap-sm leading-none"
          data-testid="card-package-picker-close"
        >
          ×
        </button>
      </div>
      {packages.map(pkg => (
        <button
          key={pkg.id}
          type="button"
          role="option"
          aria-selected="false"
          onClick={() => {
            onAdd(pkg);
            setOpen(false);
          }}
          className="block w-full text-left px-2.5 py-1.5 hover:bg-bg border-b border-border/50 last:border-0 transition-colors"
          data-testid={`card-package-picker-option-${pkg.id}`}
        >
          <div className="text-xs font-medium">{pkg.name}</div>
          <div className="text-[10px] text-muted-foreground">{pkg.packageCode}</div>
        </button>
      ))}
    </div>
  );
}

/**
 * Right column (300px fixed) — Sticky summary panel.
 *
 * Phase 4 (D9): equipment + package counts + pricing breakdown (subtotal,
 * VAT, grand total, deposit). Uses `useShallow` for selective subscription
 * (4-lens HOTFIX 2026-04-13 H1 precedens — avoid full-store re-renders).
 *
 * NEM scope (deferred): submit button + payment method selector (StickySummaryFooter
 * lives separately at wizard bottom for full submit flow — V2 right column is
 * READ-only mid-flow status).
 *
 * Mockup ref: 02-gepek-csomag-v2.html right summary panel.
 */
function V2RightSidebar() {
  const { t } = useTranslation();
  const {
    cart,
    packageSelections,
    startDate,
    endDate,
    gotoStep,
    useHourlyBilling,
    startTime,
    endTime,
    accessoryCatalogQtys,
  } = useWizardStore(
    useShallow(s => ({
      cart: s.cart,
      packageSelections: s.packageSelections,
      startDate: s.startDate,
      endDate: s.endDate,
      gotoStep: s.gotoStep,
      useHourlyBilling: s.useHourlyBilling,
      startTime: s.startTime,
      endTime: s.endTime,
      accessoryCatalogQtys: s.accessoryCatalogQtys,
    }))
  );

  // P0-1 (FE-2 slice-2): a sidebar-ár BE-authoritative — a store FE-becslés (s.pricing)
  // helyett a `POST /rentals/calculate` (#501) az SSoT (pénz-helyesség P0; a #502 óta a
  // BE az ADR-048 duration-discountot is alkalmazza). A request a store render-state-jéből
  // (pure mapper), a hook render-only + debounced (300ms).
  const calcRequest = useMemo(
    () =>
      buildRentalCalcRequest({
        cart,
        startDate,
        endDate,
        useHourlyBilling,
        startTime,
        endTime,
        accessoryCatalogQtys,
      }),
    [cart, startDate, endDate, useHourlyBilling, startTime, endTime, accessoryCatalogQtys]
  );
  const { pricing, isLoading, error } = useRentalCalcPricing(calcRequest);

  const equipmentCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const packageCount = packageSelections.length;
  // S2-6: bérlési időszak megjelenítés a summary-panelben.
  const formatPeriodDate = (d: Date): string =>
    d.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });

  return (
    <aside
      className="w-[300px] shrink-0 bg-surface border border-border rounded-md p-4 overflow-y-auto sticky top-0 self-start"
      data-testid="rental-wizard-v2-right"
    >
      <h3 className="text-sm font-semibold mb-3">{t('rental.wizard.v2.summaryTitle')}</h3>

      {/* S2-6: bérlési időszak + Módosít-link (a step-3-ra ugrik) */}
      {startDate && endDate && (
        <section className="mb-4 border-b border-border pb-3" data-testid="rental-wizard-v2-period">
          <div className="mb-1 flex items-center justify-between">
            <h4 className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {t('rental.wizard.v2.periodLabel')}
            </h4>
            <button
              type="button"
              onClick={() => gotoStep(3)}
              className="text-[11px] font-semibold text-primary hover:underline"
              data-testid="summary-edit-period"
            >
              {t('rental.wizard.v2.editPeriod')}
            </button>
          </div>
          <div className="text-xs font-medium tabular-nums" data-testid="summary-period">
            {formatPeriodDate(startDate)} – {formatPeriodDate(endDate)}
          </div>
        </section>
      )}

      {/* Counts section */}
      <section className="mb-4 space-y-1.5" data-testid="rental-wizard-v2-counts">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">{t('rental.wizard.v2.equipmentCount')}</span>
          <span className="font-medium" data-testid="summary-equipment-count">
            {equipmentCount} db
          </span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">{t('rental.wizard.v2.packageCount')}</span>
          <span className="font-medium" data-testid="summary-package-count">
            {packageCount} db
          </span>
        </div>
      </section>

      {/* S2-6 (2): gépenkénti bontás (1 sor/gép, a pricing.items per-cartItem reuse) */}
      {pricing && pricing.items.length > 0 && (
        <section
          className="mb-4 border-t border-border pt-3 space-y-1"
          data-testid="rental-wizard-v2-per-machine"
        >
          <h4 className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
            {t('rental.wizard.v2.perMachineHeader')}
          </h4>
          {pricing.items.map(it => (
            <div
              key={it.equipmentId}
              className="flex items-center justify-between gap-2 text-xs"
              data-testid="summary-per-machine-row"
            >
              <span className="truncate text-muted-foreground">{it.equipmentName}</span>
              <span className="tabular-nums shrink-0">{formatCurrency(it.itemTotal)}</span>
            </div>
          ))}
        </section>
      )}

      {/* Pricing breakdown section */}
      <section
        className="border-t border-border pt-3 space-y-1.5"
        data-testid="rental-wizard-v2-pricing"
      >
        <h4 className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
          {t('rental.wizard.v2.pricingHeader')}
        </h4>
        {error ? (
          <div className="text-[11px] text-destructive italic" data-testid="summary-pricing-error">
            {t('rental.wizard.v2.pricingError')}
          </div>
        ) : pricing ? (
          <>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{t('rental.wizard.v2.pricingSubtotal')}</span>
              <span className="tabular-nums" data-testid="summary-subtotal">
                {formatCurrency(pricing.subtotal)}
              </span>
            </div>
            {/* P0-1: ADR-048 időtartam-kedvezmény (BE-authoritative, #502 SSoT) —
                rejtve ha 0 (rövid bérlés, <7 nap). A discountTotal csak a gép-bérleti sorra. */}
            {pricing.discountTotal > 0 && (
              <div className="flex items-center justify-between text-xs text-primary">
                <span>{t('rental.wizard.v2.pricingDiscount')}</span>
                <span className="tabular-nums" data-testid="summary-discount">
                  −{formatCurrency(pricing.discountTotal)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{t('rental.wizard.v2.pricingVat')}</span>
              <span className="tabular-nums" data-testid="summary-vat">
                {formatCurrency(pricing.vatAmount)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm font-semibold border-t border-border pt-1.5 mt-1.5">
              <span>{t('rental.wizard.v2.pricingGrandTotal')}</span>
              <span className="tabular-nums" data-testid="summary-grand-total">
                {formatCurrency(pricing.grandTotal)}
              </span>
            </div>
            {pricing.depositTotal > 0 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground pt-1.5">
                <span>{t('rental.wizard.v2.pricingDeposit')}</span>
                <span className="tabular-nums" data-testid="summary-deposit">
                  {formatCurrency(pricing.depositTotal)}
                </span>
              </div>
            )}
          </>
        ) : isLoading ? (
          <div
            className="text-[11px] text-muted-foreground italic"
            data-testid="summary-pricing-loading"
          >
            {t('rental.wizard.v2.pricingLoading')}
          </div>
        ) : (
          <div
            className="text-[11px] text-muted-foreground italic"
            data-testid="summary-pricing-empty"
          >
            {t('rental.wizard.v2.pricingEmpty')}
          </div>
        )}
      </section>

      {/* v3-parity step-2: napi/heti bontás (a Hamarosan-stub kiváltása) — a
          pricing.breakdownDays már-kész adatából. 0 nap → nem renderel. */}
      {pricing && (
        <DayBreakdownSummary
          breakdownDays={pricing.breakdownDays}
          weeklyDiscountDays={pricing.weeklyDiscountDays}
          billableDays={pricing.billableDays}
        />
      )}
    </aside>
  );
}
